/**
 * ============================================================================
 * QualityMetricsDashboard — Phase 6b frontend (Quality Metrics Dashboard v2)
 * Owner: Claude 3 (Frontend & UX).
 * Route: /quality-metrics
 * Purpose: top-level Quality Metrics Dashboard. Four view personas, a hero
 *          stat row (4 tiles), and a 3×4 KPI grid (12 metrics). Clicking a
 *          KPI opens a slide-in drill-down. The polling cadence is 60 s — the
 *          metrics-svc recomputes on a 1-minute cadence and a tighter loop
 *          would mostly hit the cache.
 *
 * UX RULES (drafts/42_ux_polish_and_dual_brand_design.md):
 *   - Editorial layout, generous whitespace.
 *   - KPI cards: 14 px radius, 1 px border, hover lift ~2 px (CSS module).
 *   - Stale-data badge: muted background, monospace mini-font.
 * ============================================================================ */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardBody, CardHeader, PageHero } from '@edms/design-system';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './QualityMetricsDashboard.module.css';
import {
  useDashboardQuery,
  type DashboardView,
  type QualityMetric,
} from './useDashboardQuery';
import { KpiCard } from './KpiCard';
import { StatTile } from './StatTile';
import { ViewSelector } from './ViewSelector';
import { MetricDrillDownDrawer } from './MetricDrillDownDrawer';

export function QualityMetricsDashboard() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<DashboardView>('QA_DIRECTOR');
  const [drillTarget, setDrillTarget] = useState<QualityMetric | null>(null);

  const dashboardQ = useDashboardQuery(view);
  const data = dashboardQ.data;

  const generatedAtUtc = data?.generated_at_utc ?? new Date().toISOString();
  const metrics: QualityMetric[] = data?.metrics ?? [];
  const heroStats = data?.hero_stats ?? [];

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  return (
    <div className={wstyles.adminScreen}>
      <PageHero
        eyebrow={t('qualityMetrics.eyebrow', { defaultValue: 'Quality' })}
        title={t('qualityMetrics.title', { defaultValue: 'Quality Metrics' })}
        subtitle={t('qualityMetrics.subtitle', {
          defaultValue:
            'Real-time view of the 12 site quality KPIs, refreshed every minute.',
        })}
        actions={<ViewSelector value={view} onChange={setView} />}
      />

      {/* Hero stat row — four big tiles. */}
      <section
        className={styles.heroStatRow}
        aria-label={t('qualityMetrics.heroStat.regionAria', {
          defaultValue: "Today's hero stats",
        })}
      >
        {dashboardQ.isLoading && (
          <>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={styles.heroStatSkeleton} aria-hidden="true" />
            ))}
          </>
        )}
        {!dashboardQ.isLoading &&
          heroStats.map((s) => <StatTile key={s.key} stat={s} />)}
      </section>

      <Card>
        <CardHeader>
          <div className={styles.gridHeaderRow}>
            <h2 className={wstyles.sectionTitle}>
              {t('qualityMetrics.grid.title', { defaultValue: 'Quality KPI grid' })}
            </h2>
            <span className={styles.generatedAt} aria-live="polite">
              {t('qualityMetrics.grid.generatedAt', {
                defaultValue: 'Generated {{ts}}',
                ts: dateFmt.format(new Date(generatedAtUtc)),
              })}
            </span>
          </div>
        </CardHeader>
        <CardBody>
          {dashboardQ.isLoading && (
            <div
              className={styles.kpiGrid}
              role="status"
              aria-live="polite"
              aria-label={t('common.loading', { defaultValue: 'Loading…' })}
            >
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((i) => (
                <div key={i} className={styles.kpiSkeleton} aria-hidden="true" />
              ))}
            </div>
          )}

          {dashboardQ.isError && (
            <div className={styles.errorBanner} role="alert">
              <span>
                {t('qualityMetrics.errors.loadFailed', {
                  defaultValue: 'Could not load quality metrics.',
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => dashboardQ.refetch()}
              >
                {t('qualityMetrics.actions.retry', { defaultValue: 'Retry' })}
              </Button>
            </div>
          )}

          {!dashboardQ.isLoading && !dashboardQ.isError && metrics.length === 0 && (
            <p className={styles.emptyState}>
              {t('qualityMetrics.empty', {
                defaultValue:
                  'No metrics are configured for this view. Set thresholds in the admin.',
              })}
            </p>
          )}

          {metrics.length > 0 && (
            <div
              className={styles.kpiGrid}
              role="list"
              aria-label={t('qualityMetrics.grid.gridAria', {
                defaultValue: '12 quality KPI cards',
              })}
            >
              {metrics.map((m) => (
                <div key={m.metric_key} role="listitem">
                  <KpiCard
                    metric={m}
                    generatedAtUtc={generatedAtUtc}
                    onOpen={setDrillTarget}
                  />
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {drillTarget && (
        <MetricDrillDownDrawer
          metric={drillTarget}
          onClose={() => setDrillTarget(null)}
        />
      )}
    </div>
  );
}
